import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FeedbackRoundUser, StakeholderRole } from '@brafton/feedback-api';

/** A role anyone may be given — every role but the owner's reserved one. */
export type AssignableStakeholderRole = Exclude<StakeholderRole, StakeholderRole.Owner>;

/** A role as the dropdowns offer it. */
interface RoleOption {
  value: StakeholderRole;
  label: string;
}

/** A stakeholder resolved for display. */
interface StakeholderItem {
  email: string;
  name: string;
  initials: string;
  /** Avatar hue, spread around the wheel by position as the design does. */
  color: string;
  /** Profile photo to show instead of the initials; null when there is none (or it failed to load). */
  pictureUrl: string | null;
  role: StakeholderRole | null;
  isOwner: boolean;
  /** The roles this member's dropdown offers — Owner only for the owner. */
  roleOptions: RoleOption[];
}

/** The values collected by the add-stakeholder row. */
export interface NewStakeholder {
  email: string;
  name: string;
  role: AssignableStakeholderRole;
}

/** A role picked from a member's dropdown. */
export interface StakeholderRoleChange {
  email: string;
  role: StakeholderRole;
}

const ROLE_LABELS: Record<StakeholderRole, string> = {
  [StakeholderRole.Owner]: 'Owner',
  [StakeholderRole.Developer]: 'Developer',
  [StakeholderRole.ProjectManager]: 'Project Manager',
  [StakeholderRole.UxDesigner]: 'UX Designer',
  [StakeholderRole.UxConsultant]: 'UX Consultant',
  [StakeholderRole.LeftFeedback]: 'Left Feedback',
  [StakeholderRole.Contributor]: 'Contributor',
};

const ALL_ROLE_OPTIONS: RoleOption[] = Object.values(StakeholderRole).map((role) => ({
  value: role,
  label: ROLE_LABELS[role],
}));

const ASSIGNABLE_ROLE_OPTIONS = ALL_ROLE_OPTIONS.filter(
  (option) => option.value !== StakeholderRole.Owner,
);

/**
 * The "Project Team" card on the project detail page: the round's stakeholders as an
 * avatar-and-name row, above the mockups gallery.
 *
 * Presentational — the host supplies the round's `users` and the signed-in user, and
 * performs the writes for `add`, `remove` and `roleChange`.
 *
 * Membership is mostly not curated: the API adds whoever creates the round (as its
 * owner), submits feedback, or comments. The add row exists for the person who needs
 * to be on the thread before they have done any of those things.
 *
 * Roles come from a fixed list. A member can change their own; the owner can change
 * anyone's. The owner is flagged for good — they cannot be removed, and are the only
 * one who can pick "Owner", but may switch to another role and stay the owner.
 */
@Component({
  selector: 'app-stakeholders-panel',
  imports: [MatIconModule],
  templateUrl: './stakeholders-panel.html',
  styleUrl: './stakeholders-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StakeholdersPanel {
  /** The round's stakeholders, in the order the API returned them. */
  readonly stakeholders = input<FeedbackRoundUser[]>([]);

  /**
   * Profile photos by email (any case), e.g. the signed-in user's from auth. Members
   * without one — or whose photo fails to load — show their initials.
   */
  readonly pictureUrls = input<Record<string, string>>({});

  /** True while the list is being fetched. */
  readonly loading = input(false);

  /** True while an add, remove or role change is in flight; disables them all. */
  readonly busy = input(false);

  /** Emitted with the validated values when someone is added. */
  readonly add = output<NewStakeholder>();

  /** Emitted with the address to drop. */
  readonly remove = output<string>();

  /** Emitted when a member's role is picked from their dropdown. */
  readonly roleChange = output<StakeholderRoleChange>();

  /**
   * Emitted when a member's card is clicked — the host opens their email preferences.
   * Clicks on the card's role dropdown and remove button don't count.
   */
  readonly memberSelect = output<{ email: string; name: string }>();

  /** Whether the member cards are showing; closed, the team is a row of name chips. */
  protected readonly expanded = signal(false);

  /** Whether the add row is showing. */
  protected readonly adding = signal(false);

  protected readonly draftEmail = signal('');
  protected readonly draftName = signal('');
  protected readonly draftRole = signal<AssignableStakeholderRole>(StakeholderRole.Contributor);

  protected readonly assignableRoleOptions = ASSIGNABLE_ROLE_OPTIONS;

  /**
   * An address is all that is required — it is the identity the API stores and
   * notifies on, and a name it does not have it falls back to the address for.
   */
  protected readonly canAdd = computed(
    () => !this.busy() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.draftEmail().trim()),
  );

  /** Photos that failed to load, so those members fall back to their initials. */
  private readonly failedPictureUrls = signal<ReadonlySet<string>>(new Set());

  /** Photos keyed by lowercase email. */
  private readonly picturesByEmail = computed(
    () =>
      new Map(
        Object.entries(this.pictureUrls()).map(([pictureEmail, pictureUrl]) => [
          pictureEmail.trim().toLowerCase(),
          pictureUrl,
        ]),
      ),
  );

  /** Anyone on the team may change anyone's role; only the owner can hold Owner. */
  protected readonly items = computed<StakeholderItem[]>(() => {
    const picturesByEmail = this.picturesByEmail();
    const failedPictureUrls = this.failedPictureUrls();

    return this.stakeholders().map((stakeholder, index) => {
      const email = (stakeholder.email ?? '').trim();
      const name = (stakeholder.name ?? '').trim() || email || 'Unknown';
      const isOwner = stakeholder.isOwner === true;
      const role = stakeholder.role ?? null;
      const pictureUrl = picturesByEmail.get(email.toLowerCase()) ?? null;

      return {
        email,
        name,
        initials: this.initials(name),
        // The design's formula: hues 60 degrees apart starting at 180, so a small
        // team gets visibly distinct avatars without picking colors per person.
        color: `oklch(60% 0.17 ${(index * 60 + 180) % 360})`,
        pictureUrl: pictureUrl && !failedPictureUrls.has(pictureUrl) ? pictureUrl : null,
        role,
        isOwner,
        roleOptions: isOwner ? ALL_ROLE_OPTIONS : ASSIGNABLE_ROLE_OPTIONS,
      };
    });
  });

  /** A photo that won't load (expired, blocked) falls back to the initials. */
  protected onPictureError(pictureUrl: string): void {
    this.failedPictureUrls.update((failedUrls) => new Set([...failedUrls, pictureUrl]));
  }

  protected toggleExpanded(): void {
    this.expanded.update((isExpanded) => !isExpanded);
  }

  /** The Add toggle lives in the header, so it mustn't also collapse the section. */
  protected toggleAdding(event: Event): void {
    event.stopPropagation();
    this.adding.update((isAdding) => !isAdding);
    if (this.adding()) {
      // The add row sits in the expanded section.
      this.expanded.set(true);
    } else {
      this.resetDraft();
    }
  }

  protected submit(): void {
    if (!this.canAdd()) {
      return;
    }

    this.add.emit({
      email: this.draftEmail().trim(),
      name: this.draftName().trim(),
      role: this.draftRole(),
    });
    this.adding.set(false);
    this.resetDraft();
  }

  protected onDraftRole(event: Event): void {
    this.draftRole.set((event.target as HTMLSelectElement).value as AssignableStakeholderRole);
  }

  /**
   * The dropdown is put straight back to the stored role: the change only shows once
   * the host has saved it and passed in the refreshed list. Otherwise a failed save
   * would leave the dropdown showing a role the member does not have, since a
   * re-render with the same stored value does not touch the element.
   */
  protected onRoleChange(member: StakeholderItem, event: Event): void {
    const roleSelect = event.target as HTMLSelectElement;
    const role = roleSelect.value as StakeholderRole;
    roleSelect.value = member.role ?? '';

    if (!this.busy() && role !== member.role) {
      this.roleChange.emit({ email: member.email, role });
    }
  }

  protected onSelectMember(member: StakeholderItem): void {
    if (member.email) {
      this.memberSelect.emit({ email: member.email, name: member.name });
    }
  }

  protected onRemove(email: string): void {
    if (!this.busy() && email) {
      this.remove.emit(email);
    }
  }

  private resetDraft(): void {
    this.draftEmail.set('');
    this.draftName.set('');
    this.draftRole.set(StakeholderRole.Contributor);
  }

  /**
   * Up to two initials, matching the design's `name.split(' ').map(w => w[0])` but
   * capped: an address used as a name can otherwise produce a wall of letters.
   */
  private initials(name: string): string {
    const words = name.split(/[\s.@_-]+/).filter(Boolean);
    const letters = words.slice(0, 2).map((word) => word[0]);

    return (letters.join('') || '?').toUpperCase();
  }
}
